import { useEffect, useState } from 'react';

import type { NavSectionId } from '@/lib/constants';
import { NAV_ITEMS } from '@/lib/constants';

export function useActiveSection(isHome: boolean) {
  const [activeSection, setActiveSection] = useState<NavSectionId | null>(null);

  useEffect(() => {
    if (!isHome) return;

    const targets = NAV_ITEMS.map((i) => document.getElementById(i.id)).filter(
      (el): el is HTMLElement => Boolean(el),
    );

    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0))[0];

        if (!visible?.target?.id) return;
        setActiveSection(visible.target.id as NavSectionId);
      },
      {
        root: null,
        rootMargin: '-96px 0px -55% 0px',
        threshold: [0.15, 0.25, 0.4, 0.6],
      },
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isHome]);

  // Reset active section when at the top
  useEffect(() => {
    if (!isHome) return;
    const onScroll = () => {
      if (window.scrollY < 40) setActiveSection(null);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  return [activeSection, setActiveSection] as const;
}
